import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, User as UserIcon, LogOut, MapPin, Phone, Mail, CreditCard, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import StripeCardManager from "@/components/StripeCardManager";
import { BUSINESS } from "@/lib/api";

export default function Profile() {
  const { user, loading, updateProfile, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "", city: BUSINESS.serviceAreas[0] });

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/profile");
  }, [loading, user, navigate]);

  // Handle return from Stripe Hosted Checkout (setup mode fallback).
  // Stripe redirects back with ?card_saved=1 on success or ?card_cancelled=1 on cancel.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("card_saved") === "1") {
      toast.success("Card saved securely via Stripe. You're ready to schedule pickups.");
      refresh();
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("card_cancelled") === "1") {
      toast.info("Card setup was cancelled. You can try again whenever you're ready.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [user, refresh]);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || BUSINESS.serviceAreas[0],
      });
    }
  }, [user]);

  const goToSchedule = () => navigate("/#schedule");

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateProfile(form);
      if (user?.has_card) {
        toast.success("Profile saved — taking you to scheduling…");
        setTimeout(goToSchedule, 800);
      } else {
        toast.success("Profile saved. Now add a card on file to schedule pickups.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile");
    } finally {
      setBusy(false);
    }
  };

  const onCardSaved = async () => {
    await refresh();
    const profileFilled = !!(form.name && form.phone && form.address);
    if (profileFilled) {
      toast.success("Card saved — taking you to scheduling…");
      setTimeout(goToSchedule, 800);
    } else {
      toast.success("Card saved. Fill in your details to schedule a pickup.");
    }
  };

  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-7 w-7 animate-spin text-brand-blue" />
      </div>
    );
  }

  const profileComplete = form.name && form.phone && form.address;
  const ready = profileComplete && user.has_card;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="profile-page">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-5 py-24 pt-32">
        {/* Greeting */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-brand-navy">
              Hi, {form.name?.split(" ")[0] || "there"}
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              <Mail className="h-3.5 w-3.5 inline mr-1.5" />
              {user.email}
            </p>
          </div>
          <Button variant="outline" onClick={onLogout} className="gap-2" data-testid="profile-logout-button">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>

        {/* Readiness banner */}
        <Card className={`p-5 mb-6 border-l-4 ${ready ? "border-l-emerald-500 bg-emerald-50" : "border-l-amber-500 bg-amber-50/40"}`} data-testid="profile-readiness">
          {ready ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="font-bold text-emerald-900 text-lg">You're all set 🎉</div>
                <div className="text-sm text-emerald-800 mt-0.5">Your profile and card on file are ready — pick a pickup time now.</div>
              </div>
              <Button asChild size="lg" className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-base h-12 px-6 font-semibold" data-testid="profile-schedule-cta">
                <a href="/#schedule">Schedule a pickup <ArrowRight className="h-5 w-5" /></a>
              </Button>
            </div>
          ) : (
            <div>
              <div className="font-semibold text-amber-900">Almost there</div>
              <div className="text-sm text-amber-800">
                {profileComplete ? "Add a card on file so we can charge you after we process your items." : "Fill in your name, phone, and address — then add a card on file to schedule pickups."}
              </div>
            </div>
          )}
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Profile form */}
          <Card className="p-6" data-testid="profile-form-card">
            <h2 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-brand-blue" /> Your details
            </h2>
            <form onSubmit={save} className="space-y-3.5">
              <div>
                <Label htmlFor="p-name">Full name</Label>
                <Input id="p-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
              </div>
              <div>
                <Label htmlFor="p-phone">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="p-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="pl-9" data-testid="profile-phone-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="p-address">Pickup address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="p-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="pl-9" data-testid="profile-address-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="p-city">City</Label>
                <select
                  id="p-city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  data-testid="profile-city-select"
                >
                  {BUSINESS.serviceAreas.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <Button type="submit" disabled={busy} className="w-full gap-2" data-testid="profile-save-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {busy ? "Saving…" : "Save profile"}
              </Button>
            </form>
          </Card>

          {/* Payment methods */}
          <Card className="p-6" data-testid="payment-methods-card">
            <h2 className="font-semibold text-lg text-slate-900 mb-1 flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-brand-blue" /> Card on file
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              We charge your card only after we process your dry cleaning, never before pickup.
            </p>
            <StripeCardManager onChanged={onCardSaved} />
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
